import React from "react";
import { TextField, FormControl, InputLabel, Select, MenuItem, Box, Typography } from "@mui/material";
import styled from "@emotion/styled";
import AppDatePicker from "../AppDatePicker";

const FormField = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const Row = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
    },
}));

const HalfWidthField = styled(FormField)(({ theme }) => ({
    width: "50%",
    [theme.breakpoints.down("sm")]: {
        width: "100%",
    },
}));

const RELATIVE_OPTIONS = [
    { value: "startOfMonth", label: "Start of month" },
    { value: "endOfMonth", label: "End of month" },
    { value: "startOfNextMonth", label: "Start of next month" },
    { value: "endOfNextMonth", label: "End of next month" },
    { value: "startOfQuarter", label: "Start of quarter" },
    { value: "endOfQuarter", label: "End of quarter" },
];

const StepScheduling = ({ form, onChange }) => {
    return (
        <>
            <FormField>
                <FormControl fullWidth>
                    <InputLabel>Date mode</InputLabel>
                    <Select name="templateDateType" value={form.templateDateType} onChange={onChange} label="Date mode">
                        <MenuItem value="specific">Specific dates</MenuItem>
                        <MenuItem value="offset">Offsets (days from execution date)</MenuItem>
                        <MenuItem value="relative">Relative (calculated at execution time)</MenuItem>
                    </Select>
                </FormControl>
            </FormField>

            {form.templateDateType === "specific" && (
                <Row>
                    <HalfWidthField>
                        <AppDatePicker
                            label="Start Date"
                            name="startDate"
                            value={form.startDate}
                            onChange={(v, n) => onChange({ target: { name: n, value: v } })}
                        />
                    </HalfWidthField>
                    <HalfWidthField>
                        <AppDatePicker
                            label="Due Date"
                            name="dueDate"
                            value={form.dueDate}
                            onChange={(v, n) => onChange({ target: { name: n, value: v } })}
                        />
                    </HalfWidthField>
                </Row>
            )}

            {form.templateDateType === "offset" && (
                <Row>
                    <HalfWidthField>
                        <TextField
                            fullWidth
                            type="number"
                            label="Start offset (days)"
                            name="templateStartOffset"
                            value={form.templateStartOffset}
                            onChange={onChange}
                            helperText="Days after the execution start date"
                        />
                    </HalfWidthField>
                    <HalfWidthField>
                        <TextField
                            fullWidth
                            type="number"
                            label="Due offset (days)"
                            name="templateDueOffset"
                            value={form.templateDueOffset}
                            onChange={onChange}
                            helperText="Days after the execution start date"
                        />
                    </HalfWidthField>
                </Row>
            )}

            {form.templateDateType === "relative" && (
                <>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Dates are resolved relative to the execution date. For example, "Start of month" on a template executed April 20 resolves to April 1. Offsets are then added on top of the resolved anchor.
                    </Typography>
                    <Row>
                        <HalfWidthField>
                            <FormControl fullWidth>
                                <InputLabel>Start date anchor</InputLabel>
                                <Select
                                    name="templateStartRelative"
                                    value={form.templateStartRelative || ""}
                                    onChange={onChange}
                                    label="Start date anchor"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {RELATIVE_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </HalfWidthField>
                        <HalfWidthField>
                            <FormControl fullWidth>
                                <InputLabel>Due date anchor</InputLabel>
                                <Select
                                    name="templateDueRelative"
                                    value={form.templateDueRelative || ""}
                                    onChange={onChange}
                                    label="Due date anchor"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {RELATIVE_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </HalfWidthField>
                    </Row>
                    <Row>
                        <HalfWidthField>
                            <TextField
                                fullWidth
                                type="number"
                                label="Start offset (days)"
                                name="templateStartOffset"
                                value={form.templateStartOffset}
                                onChange={onChange}
                                helperText="Days added after the start anchor date"
                            />
                        </HalfWidthField>
                        <HalfWidthField>
                            <TextField
                                fullWidth
                                type="number"
                                label="Due offset (days)"
                                name="templateDueOffset"
                                value={form.templateDueOffset}
                                onChange={onChange}
                                helperText={form.templateDueRelative ? "Days added after the due anchor date" : "Days added after the resolved start date"}
                            />
                        </HalfWidthField>
                    </Row>
                </>
            )}
        </>
    );
};

export default StepScheduling;
